import { Product, ProductGroup, WithContext } from 'schema-dts';
import { ShopTypes } from '~/lib/data-contract';
import { capitalizeWholeWords } from '~/shared/utils/helpers';
import { AvailabilityTypes } from '../availability';
import { DeliveryPriceDetails } from '~/lib/data-contract/auto-generated/shop-api-model';

const WEBSITE_URL = process.env.NEXT_PUBLIC_WEBSITE_URL;

export function productSchema(
    props: ProductProps,
): WithContext<Product | ProductGroup> | undefined {
    const { brandUrl, category, freight, image, price, product, stockDetails } = props;

    const info = product?.item || product;
    const name1 = info?.name ? info?.name + ' ' : '';
    const name2 = info?.name2 ? info?.name2 : '';
    const name = (name1 + name2).trim();

    const fullBrandUrl = brandUrl ? WEBSITE_URL + brandUrl.toLowerCase() : '';

    const shortDescription = product?.shortDescription
        ? product?.shortDescription + '\r\n\r\n'
        : '';
    const longDescription = product?.longDescription ? product?.longDescription : '';
    const description = shortDescription + longDescription;

    const currentVariant = product?.variants?.filter(
        (variant) => variant.itemNumber === product?.primaryItemNumber,
    )[0];

    const availability = availabilityStatus({
        stockDetails: stockDetails,
        variant: currentVariant,
    });

    const isProductGroup: boolean = (product?.variants?.length || 0) > 1;

    const productGroupID = capitalizeWholeWords(product?.name || '', true);

    const hasVariant: Product[] =
        product?.variants?.map(
            (variant) =>
                variantSchema(
                    product?.brand ?? '',
                    fullBrandUrl,
                    category ?? '',
                    description,
                    variant,
                ) as Product,
        ) || [];

    const variesBy = capitalizeWholeWords(product?.variants?.[0]?.variantType || '', true);

    const shownPrice = price?.price?.priceInclVat
        ? (Math.ceil(price?.price?.priceInclVat * 100) / 100)
              .toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              })
              .replace(',', '')
        : undefined;

    const schema = {
        '@context': 'https://schema.org',
        '@type': isProductGroup ? 'ProductGroup' : 'Product',
        name: capitalizeWholeWords(name, true),
        image: image?.link || '',
        description: description.trim(),
        category,
        mpn: product?.item?.supplierItemNumber,
        sku: currentVariant?.itemNumber || product?.item?.itemNumber || product?.primaryItemNumber,
        color: currentVariant?.color || product?.item?.color,
        url: currentVariant?.itemCanonicalUrl || product?.item?.itemCanonicalUrl,
        gtin: currentVariant?.ean,
        brand: {
            '@type': 'Brand',
            name: product?.brand,
            url: fullBrandUrl,
        },
        offers: {
            '@type': 'Offer',
            url: currentVariant?.itemCanonicalUrl || product?.item?.itemCanonicalUrl,
            priceCurrency: price?.currency,
            availability: availability,
            price: shownPrice,
            itemCondition: 'https://schema.org/NewCondition',
            seller: {
                '@type': 'Organization',
                name: 'Johannes Fog A/S',
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                doesNotShip:
                    availability === AvailabilityTypes.Discontinued ||
                    availability === AvailabilityTypes.InStoreOnly,
                deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    transitTime: {
                        '@type': 'QuantitativeValue',
                        minValue: stockDetails?.online?.title?.values?.[0]?.value,
                        maxValue: stockDetails?.online?.title?.values?.[1]?.value,
                        unitCode: 'DAY',
                    },
                },
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: 'DK',
                },
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    maxValue: Number(freight?.price?.priceInclVat),
                    currency: 'DKK',
                },
            },
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                returnPolicyCategory: 'MerchantReturnFiniteReturnWindow',
                merchantReturnDays: 30,
                returnMethod: [
                    'https://schema.org/ReturnInStore',
                    'https://schema.org/ReturnByMail',
                ],
                returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
                applicableCountry: 'DK',
            },
        },
    };

    return isProductGroup
        ? ({
              ...schema,
              productGroupID,
              hasVariant,
              variesBy,
          } as WithContext<ProductGroup>)
        : (schema as WithContext<Product>);
}

export default productSchema;

export interface ProductProps {
    brandUrl?: string;
    category?: string;
    freight?: DeliveryPriceDetails;
    image?: ShopTypes.ProductQueryResource;
    product?: ShopTypes.ProductQueryProduct;
    price?: ShopTypes.SalesUnitPrice;
    stockDetails?: ShopTypes.ProductStockDetailsResponse;
}

interface AvailabilityProps {
    stockDetails?: ShopTypes.ProductStockDetailsResponse;
    variant?: ShopTypes.ProductQueryProductItem;
}

function availabilityStatus({ stockDetails, variant }: AvailabilityProps) {
    if (!stockDetails) {
        return;
    }

    switch (true) {
        case variant?.itemStatus === ShopTypes.ItemStatusValues.Discontinued:
            return AvailabilityTypes.Discontinued;
        case stockDetails?.online?.status === ShopTypes.StockStatus.LowStock:
        case stockDetails?.pickup?.status === ShopTypes.StockStatus.LowStock:
            return AvailabilityTypes.LimitedAvailability;
        case stockDetails?.online?.status === ShopTypes.StockStatus.OutOfStock &&
            stockDetails?.pickup?.status === ShopTypes.StockStatus.OutOfStock:
            return AvailabilityTypes.OutOfStock;
        case stockDetails?.online?.status === ShopTypes.StockStatus.InStock &&
            stockDetails?.pickup?.status !== ShopTypes.StockStatus.InStock &&
            stockDetails?.pickup?.status !== ShopTypes.StockStatus.LowStock:
            return AvailabilityTypes.OnlineOnly;
        case stockDetails?.online?.status !== ShopTypes.StockStatus.InStock &&
            stockDetails?.online?.status !== ShopTypes.StockStatus.LowStock &&
            stockDetails?.pickup?.status === ShopTypes.StockStatus.InStock:
            return AvailabilityTypes.InStoreOnly;
        default:
            return AvailabilityTypes.InStock;
    }
}

function variantSchema(
    brand: string,
    brandUrl: string,
    category: string,
    description: string,
    variant: ShopTypes.ProductQueryProductItem,
) {
    const { color, ean, images, itemCanonicalUrl, itemNumber, name, name2, supplierItemNumber } =
        variant;
    const displayName = capitalizeWholeWords(
        ((name ? name + ' ' : '') + (name2 ? name2 : '')).trim(),
        true,
    );

    return {
        '@type': 'Product',
        name: displayName,
        image: images?.[0]?.link,
        description: description.trim(),
        category,
        mpn: supplierItemNumber,
        sku: itemNumber,
        color: color,
        url: itemCanonicalUrl,
        gtin: ean,
        brand: {
            '@type': 'Brand',
            name: brand,
            url: brandUrl,
        },
    };
}
